import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import { Issuer } from "src/utils/auth";
import { tokenApi } from "src/api";

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SIGN_IN"] = "SIGN_IN";
  ActionType["SIGN_UP"] = "SIGN_UP";
  ActionType["SIGN_OUT"] = "SIGN_OUT";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  signInKakao: () => Promise.resolve(),
  signInNaver: () => Promise.resolve(),
  signInGoogle: () => Promise.resolve(),
  exist: () => Promise.resolve(),
  confirm: () => Promise.resolve(),
  reset: () => Promise.resolve(),
  forgot: () => Promise.resolve(),
  refreshToken: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = tokenApi.getAccessToken();

      if (accessToken) {
        let user;
        try {
          user = await authApi.init();
        } catch (initError) {
          const errorCode = initError?.response?.data?.code || initError?.code;
          const storedRefreshToken = tokenApi.getRefreshToken();
          if (errorCode !== "AU002" || !storedRefreshToken) throw initError;

          // 앱 초기화는 RequestProvider의 interceptor보다 먼저 실행될 수 있으므로
          // 만료 토큰 복원을 interceptor에만 의존하지 않고 여기서 직접 수행한다.
          const tokens = await authApi.refreshToken({ refreshToken: storedRefreshToken });
          tokenApi.setAccessToken(tokens.accessToken);
          tokenApi.setRefreshToken(tokens.refreshToken);
          user = await authApi.init();
        }

        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      tokenApi.clearTokens();
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [dispatch]);

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const signIn = useCallback(
    async (email, password) => {
      const { accessToken, refreshToken } = await authApi.signIn({
        email,
        password,
      });

      tokenApi.setAccessToken(accessToken);
      tokenApi.setRefreshToken(refreshToken);

      const user = await authApi.me();
      console.log("me: %O", user);

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });
    },
    [dispatch]
  );

  const signInKakao = useCallback(
    async (code) => {
      const { accessToken, refreshToken, isNewUser } =
        await authApi.signInKakao({
          code,
          redirectUri: window.location.origin + "/auth/jwt/kakao-callback",
        });

      tokenApi.setAccessToken(accessToken);
      tokenApi.setRefreshToken(refreshToken);

      const user = await authApi.me();
      console.log("me: %O", user);

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });

      return { isNewUser, user };
    },
    [dispatch]
  );

  const signInNaver = useCallback(
    async (code) => {
      const { accessToken, refreshToken, isNewUser } =
        await authApi.signInNaver({ code });

      tokenApi.setAccessToken(accessToken);
      tokenApi.setRefreshToken(refreshToken);

      const user = await authApi.me();

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });

      return { isNewUser, user };
    },
    [dispatch]
  );

  const signInGoogle = useCallback(
    async (code) => {
      const { accessToken, refreshToken, isNewUser } =
        await authApi.signInGoogle({
          code,
          redirectUri: window.location.origin + "/auth/jwt/google-callback",
        });

      tokenApi.setAccessToken(accessToken);
      tokenApi.setRefreshToken(refreshToken);

      const user = await authApi.me();

      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });

      return { isNewUser, user };
    },
    [dispatch]
  );

  const signUp = useCallback(async (email, name, password) => {
    // return is {email}
    return await authApi.signUp({ email, name, password });
  }, []);

  const exist = useCallback(async (email) => {
    // return is boolean
    return await authApi.exist({ email });
  }, []);

  const forgot = useCallback(async (email) => {
    return await authApi.forgot({ email });
  }, []);

  const reset = useCallback(async (resetCode, password) => {
    return await authApi.reset({ resetCode, password });
  }, []);

  const refreshToken = useCallback(async () => {
    const refreshToken = tokenApi.getRefreshToken();
    const tokens = await authApi.refreshToken({ refreshToken });
    tokenApi.setAccessToken(tokens.accessToken);
    tokenApi.setRefreshToken(tokens.refreshToken);
  }, []);

  const confirm = useCallback(
    async (confirmCode) => {
      const { accessToken, refreshToken } = await authApi.confirm({
        confirmCode,
      });

      tokenApi.setAccessToken(accessToken);
      tokenApi.setRefreshToken(refreshToken);

      const user = await authApi.me();

      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });
    },
    [dispatch]
  );

  const refreshUser = useCallback(async () => {
    const user = await authApi.me();
    dispatch({
      type: ActionType.SIGN_IN,
      payload: { user },
    });
  }, [dispatch]);

  const signOut = useCallback(async () => {
    tokenApi.clearTokens();
    dispatch({ type: ActionType.SIGN_OUT });
  }, [dispatch]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signUp,
        signOut,
        signInKakao,
        signInNaver,
        signInGoogle,
        exist,
        confirm,
        forgot,
        reset,
        refreshToken,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
